import { EntityType, Task } from "@prisma/client";

import { DateInputValueFormat } from "~/components/datetime";
import { Form, FormProps } from "~/components/Form";
import { DatePickerInput, Flex, Select, showNotification, Textarea, TextInput, type UseFormReturnType, useForm, zodResolver } from "~/ui";
import { api } from "~/utils/api";

import { UpsertTaskSchemaBaseType, upsertTaskSchemaBase } from "../schema";

export type TaskFormUser = {
  name: string;
  email: string | null;
  id: string;
};

type TaskFormInput = {
  assignedToId?: string | null;
  description?: string | null;
  dueDate?: Date | null;
  title: string;
};

type TaskFormPropsBase = {
  users: TaskFormUser[];
  onCancel: () => void;
  onSuccess: () => void;
};

type TaskFormProps = TaskFormPropsBase & {
  error: FormProps<CreateFormProps | UpdateFormProps>["mutationError"];
  isError: boolean;
  isLoading: boolean;
  form: UseFormReturnType<UpsertTaskSchemaBaseType>;
  onSubmit: (f: UpsertTaskSchemaBaseType) => void;
};

type CreateFormProps = TaskFormPropsBase & {
  entityId?: string | null;
  entityType?: EntityType | null;
};

type UpdateFormProps = TaskFormPropsBase & {
  task: Task;
};

const TaskForm = (props: TaskFormProps): JSX.Element => {
  return (
    <Form
      feedback={[{ message: "Something went wrong while saving your Task", visible: props.isError }]}
      form={props.form}
      mutationError={props.error}
      submitting={props.isLoading}
      onCancel={props.onCancel}
      onSubmit={props.onSubmit}
    >
      <Flex direction="column" gap="sm">
        <TextInput {...props.form.getInputProps("title")} label="Title" placeholder="ie: Call John about Dinner" withAsterisk size="xs" />
        <Textarea
          {...props.form.getInputProps("description")}
          label="Description"
          placeholder="(Optional) ie: Don't forget to bring your wallet!"
          size="xs"
        />
      </Flex>
      <Flex direction="row" gap="sm">
        <Select
          {...props.form.getInputProps("assignedToId")}
          label="Assigned to"
          placeholder="Select a User to assign"
          data={props.users.map(x => ({
            label: `${x.name} (${x.email})`,
            value: x.id,
          }))}
          size="xs"
        />
        <DatePickerInput
          {...props.form.getInputProps("dueDate")}
          label="Due Date"
          placeholder="When is this Task due?"
          valueFormat={DateInputValueFormat}
          size="xs"
        />
      </Flex>
    </Form>
  );
};

export const CreateTaskForm = (props: CreateFormProps): JSX.Element => {
  const { error, isError, isPending: isLoading, mutate } = api.tasks.create.useMutation();

  const form = useForm<UpsertTaskSchemaBaseType>({
    validate: zodResolver(upsertTaskSchemaBase),
    initialValues: {
      assignedToId: undefined,
      description: "",
      dueDate: undefined,
      title: "",
    },
  });

  const handleSubmit = (f: UpsertTaskSchemaBaseType) => {
    mutate(
      {
        ...f,
        entityId: props.entityId,
        entityType: props.entityType,
      },
      {
        onSuccess: t => {
          showNotification({
            autoClose: 5000,
            color: "green",
            title: "Task created",
            message: `Your task ${t.title} was created successfully`,
          });
          props.onSuccess();
        },
      },
    );
  };

  return (
    <TaskForm
      error={error}
      isError={isError}
      isLoading={isLoading}
      form={form}
      onSubmit={handleSubmit}
      users={props.users}
      onCancel={props.onCancel}
      onSuccess={props.onSuccess}
    />
  );
};

export const UpdateTaskForm = (props: UpdateFormProps): JSX.Element => {
  const { error, isError, isPending: isLoading, mutate } = api.tasks.update.useMutation();

  const form = useForm<UpsertTaskSchemaBaseType>({
    validate: zodResolver(upsertTaskSchemaBase),
    initialValues: {
      assignedToId: props.task.assignedToId,
      description: props.task.description,
      dueDate: props.task.dueDate,
      title: props.task.title,
    },
  });

  const handleSubmit = (f: TaskFormInput) => {
    mutate(
      {
        ...f,
        taskId: props.task.id,
      },
      {
        onSuccess: t => {
          showNotification({
            autoClose: 5000,
            color: "green",
            title: "Task created",
            message: `Your task ${t.title} was updated successfully`,
          });
          props.onSuccess();
        },
      },
    );
  };

  return (
    <TaskForm
      error={error}
      isError={isError}
      isLoading={isLoading}
      form={form}
      onSubmit={handleSubmit}
      users={props.users}
      onCancel={props.onCancel}
      onSuccess={props.onSuccess}
    />
  );
};
